import _ from "lodash";
import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";

// Hook
import { useI18n } from "Hooks";

// components
import AIChat from "UIComponents/AIChat";
import ContextSelectionModal from "./components/ContextSelectionModal";

// third party
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

// utils
import {
  getEntityAndFieldType,
  getInsertAttachmentElement,
  getFooterOptions,
} from "./modules/Utils";
import { wrapPlaceholderWithSpan } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/components/PromptCards/PromptCards";

// constants
import { MODAL_STATE } from "./modules/Constants";
import { ASSESSMENT, UNIT } from "Constants/stringConstants";

// modules
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";
import { getAssessmentDetailsForAIEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import { getUnitPlanCollaboratorsQuery } from "../../modules/LessonPlanningQueries";

// scss

const Footer = props => {
  const {
    assessmentDetails,
    assessmentTemplate,
    placeholder,
    curriculumType,
    assessmentType,
    getAssessmentObj,
    isAITutor,
    unitPlanTemplate,
    unitPlanDetails,
    selectedAction,
    conversationAttachments,
    contextFields,
    setSelectedAction,
    prompts,
  } = props;

  const { t } = useI18n();

  // ======= states ========

  const [selectedField, setSelectedField] = useState({
    modalState: MODAL_STATE.NULL,
  });

  // ====== variables =======

  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};
  const assessmentTypeLabel = typeLabel || typeLabel2;

  // ======== functions ========

  const onClickContextOption = params => {
    const { step, config } = params;

    const { field, type } = getEntityAndFieldType({
      ...params,
      assessmentDetails,
      unitPlanDetails,
    });

    const isUnitDependent = type === UNIT;

    const stepList = isUnitDependent
      ? unitPlanTemplate?.step_list
      : assessmentTemplate?.step_list;

    // if step is defined for any pre-requisite then render all the fields of step
    if (stepList?.[step]) {
      const stepFields = _.get(stepList, `${step}.fields`, []);
      setSelectedField({
        fields: _.map(stepFields, field => ({ field })),
        step,
        isUnitDependent,
        modalState: MODAL_STATE.OPEN,
      });
    } else {
      setSelectedField({
        fields: [{ field, type, config }],
        step,
        isUnitDependent,
        modalState: MODAL_STATE.OPEN,
      });
    }
  };

  const onAttachmentsUpdate = (
    {
      id,
      type,
      label,
      valueRef,
      selectedAttachmentId,
      prevAttachmentId,
      attachments,
    },
    callback = _.noop
  ) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = valueRef.current;

    // handle attachment delete
    if (type === "DELETE") {
      const attachmentElement = tempElement.querySelector(
        `[data-attachment-id="${id}"]`
      );

      // When deleting the last attachment, replace the placeholder with 'insert attachment' element
      // else just remove the attachment
      if (_.size(attachments) === 1) {
        const insertAttachmentEle = getInsertAttachmentElement(selectedAction);
        if (insertAttachmentEle) {
          attachmentElement?.replaceWith(insertAttachmentEle);
        } else {
          attachmentElement?.remove();
        }
      } else {
        attachmentElement?.remove();
      }
    } else {
      // editing existing attachment
      if (selectedAttachmentId) {
        const elemToUpdate = tempElement.querySelector(
          `[data-attachment-id="${prevAttachmentId}"]`
        );
        // Locate nodes with id of 'attachment-content' and update its innerText
        _.forEach(elemToUpdate.children, node => {
          if (node.id === "attachment-content") {
            node.innerText = ` ${label} `;
          }
        });

        // update the selected attachment id of the node
        elemToUpdate.dataset.attachmentId = id;
      } else {
        // When adding first attachment, the 'insert attachment' btn is replaced
        if (_.isEmpty(attachments)) {
          const insertAttachmentEle = tempElement.querySelector(
            "#attachment-text"
          );

          // Replace insertAttachmentEle with textNode
          if (insertAttachmentEle) {
            const textNode = document.createTextNode(`##${label}##`);
            insertAttachmentEle.replaceWith(textNode);
          }
        } else {
          // append the new attachment label at appropriate position
          const textNode = document.createTextNode(`##${label}##`);
          const lastAttachmentElement = tempElement.querySelector(
            "#attachment-text:last-child"
          );
          lastAttachmentElement?.insertAdjacentText(
            "afterend",
            textNode.textContent
          );
        }
      }
    }

    const updatedLabelWithSpan = wrapPlaceholderWithSpan({
      t,
      label: tempElement.innerHTML,
      attachmentId: id,
      showEditIcon: true,
    });

    callback({ updatedLabel: updatedLabelWithSpan });
  };

  const onClose = () => {
    setSelectedField({ modalState: MODAL_STATE.CLOSED });
  };

  const footerOptions = getFooterOptions({
    conversationAttachments,
    isAITutor,
    prompts,
  });

  const onChangeSelectedPrompt = useCallback(prompt => {
    setSelectedAction(prompt);
  }, []);

  return (
    <>
      <AIChat.Footer
        {...props}
        placeholder={placeholder}
        showAudioTranscript={true}
        footerOptions={footerOptions}
        contextOptions={contextFields}
        selectedPrompt={selectedAction}
        onAttachmentsUpdate={onAttachmentsUpdate}
        onClickContextOption={onClickContextOption}
        onChangeSelectedPrompt={onChangeSelectedPrompt}
      />

      {!_.isEmpty(selectedField?.fields) &&
        ReactDOM.createPortal(
          <ContextSelectionModal
            t={t}
            {...props}
            {...(selectedField || {})}
            onClose={onClose}
            unitPlanDetails={unitPlanDetails}
            assessmentTypeLabel={assessmentTypeLabel}
          />,
          document.body
        )}
    </>
  );
};

const mapActionCreators = {
  getAssessmentObj,
};

export default compose(
  connect(null, mapActionCreators),
  getAssessmentDetailsForAIEnhancer,
  graphql(getUnitPlanCollaboratorsQuery, {
    name: "getUnitPlanCollaborators",
    skip: ({ unitPlanDetails }) => _.isEmpty(unitPlanDetails?.unitPlanId),
    options: ({ unitPlanDetails, collaboratorsFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: unitPlanDetails?.unitPlanId, collaboratorsFilter },
    }),
  })
)(Footer);
